import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { createHash, randomInt, timingSafeEqual } from 'node:crypto';
import { EMPTY, catchError, interval } from 'rxjs';
import { RegistrationTooManyAttemptsError } from '../errors/RegistrationTooManyAttemptsError.js';
import { RegistrationWrongCodeError } from '../errors/RegistrationWrongCodeError.js';
import { mongo } from '../server/mongo.js';

const ticketCollectionName = 'buchungsheldtickets';
const sendLimitCollectionName = 'buchungsheldotpsends';
const ticketLifetimeMs = 10 * 60 * 1000;
const sendWindowMs = 15 * 60 * 1000;
const resendDelayMs = 30 * 1000;
const maxSendsPerWindow = 5;
const resendError = 'Bitte warten Sie einen Moment, bevor Sie einen neuen Code anfordern.';

interface BuchungsheldOTPTicket {
  _id: string;
  otpHash: string;
  userID: string | null;
  emailLower: string;
  attempts: number;
  timestamp: Date;
}

// Send history is separate from tickets so consuming a code cannot reset either send limit.
interface OTPSendLimit {
  _id: string;
  sentAt: Date[];
  lastSentAt: Date;
  version: number;
}

type OTPTarget = { userID?: string; emailLower?: string; matchingUserID?: string };

const hashOTP = (otp: string) => createHash('sha256').update(otp).digest('hex');

function ticketFilter(target: OTPTarget) {
  if (target.userID) return { userID: target.userID };
  if (!target.emailLower) throw new RegistrationWrongCodeError('target missing');
  return target.matchingUserID
    ? { $or: [{ emailLower: target.emailLower }, { userID: target.matchingUserID }] }
    : { emailLower: target.emailLower };
}

async function reserveSend(userID: string | null, emailLower: string): Promise<void> {
  const col = mongo().collection<OTPSendLimit>(sendLimitCollectionName);
  const id = userID ? `user:${userID}` : `email:${emailLower}`;

  // Versioned compare-and-swap keeps concurrent sends from bypassing the limits.
  while (true) {
    const now = new Date();
    const previous = await col.findOne({ _id: id });
    const recent = previous?.sentAt.filter(sent => sent.getTime() > now.getTime() - sendWindowMs) ?? [];
    if (recent.length >= maxSendsPerWindow || (recent.at(-1)?.getTime() ?? 0) > now.getTime() - resendDelayMs)
      throw new ExpectedError(resendError);

    if (!previous) {
      try {
        await col.insertOne({ _id: id, sentAt: [now], lastSentAt: now, version: 1 });
        return;
      } catch (error) {
        if ((error as { code?: number }).code !== 11000) throw error;
        continue;
      }
    }

    const result = await col.updateOne(
      { _id: id, version: previous.version },
      { $set: { sentAt: [...recent, now], lastSentAt: now }, $inc: { version: 1 } },
    );
    if (result.modifiedCount) return;
  }
}

export const createOTP = async ({ userID, emailLower }: { userID: string | null; emailLower: string }) => {
  emailLower = emailLower.trim().toLowerCase();
  if (!emailLower) throw new RegistrationWrongCodeError('target missing');
  await reserveSend(userID, emailLower);

  const otp = randomInt(0, 1_000_000).toString().padStart(6, '0');
  const col = mongo().collection<BuchungsheldOTPTicket>(ticketCollectionName);
  await col.deleteMany(userID ? { $or: [{ userID }, { emailLower }] } : { emailLower });
  await col.insertOne({
    _id: A.getGloballyUniqueID(),
    otpHash: hashOTP(otp),
    userID,
    emailLower,
    attempts: 0,
    timestamp: new Date(),
  });
  return otp;
};

export const useOTP = async (otp: string, target: OTPTarget) => {
  const col = mongo().collection<BuchungsheldOTPTicket>(ticketCollectionName);
  const filter = ticketFilter(target);
  const submittedHash = Buffer.from(hashOTP(otp), 'hex');

  while (true) {
    const ticket = await col.findOne(filter, { sort: { timestamp: -1 } });
    if (!ticket) throw new RegistrationWrongCodeError('ticket not found');
    if (ticket.timestamp.getTime() <= Date.now() - ticketLifetimeMs) {
      await col.deleteOne({ _id: ticket._id });
      throw new RegistrationWrongCodeError('ticket expired');
    }
    // Discard tickets written by the old plaintext schema during deployment.
    if (typeof ticket.otpHash !== 'string' || typeof ticket.attempts !== 'number') {
      await col.deleteOne({ _id: ticket._id });
      throw new RegistrationWrongCodeError('ticket invalid');
    }
    if (ticket.attempts >= 5) {
      await col.deleteOne({ _id: ticket._id });
      throw new RegistrationTooManyAttemptsError();
    }

    const storedHash = Buffer.from(ticket.otpHash, 'hex');
    if (storedHash.length !== submittedHash.length || !timingSafeEqual(storedHash, submittedHash)) {
      const result = await col.updateOne({ _id: ticket._id, attempts: ticket.attempts }, { $inc: { attempts: 1 } });
      if (!result.modifiedCount) continue;
      if (ticket.attempts + 1 >= 5) {
        await col.deleteOne({ _id: ticket._id });
        throw new RegistrationTooManyAttemptsError();
      }
      throw new RegistrationWrongCodeError('code mismatch');
    }

    const result = await col.deleteOne({ _id: ticket._id, attempts: ticket.attempts });
    if (!result.deletedCount) continue;
    await col.deleteMany(filter);
    return { userID: ticket.userID, emailLower: ticket.emailLower };
  }
};

export const deleteOTP = async ({
  userID,
  emailLower,
  otp,
}: {
  userID: string | null;
  emailLower: string;
  otp: string;
}) => {
  const col = mongo().collection<BuchungsheldOTPTicket>(ticketCollectionName);
  await col.deleteOne({ userID, emailLower, otpHash: hashOTP(otp) });
};

/** Regularly remove expired codes and send histories whose entire limit window has elapsed. */
export const registerOTPTokenCleanupScheduler = () => {
  const cleanup = async () => {
    const expiredBefore = new Date(Date.now() - ticketLifetimeMs);
    const result = await mongo()
      .collection<BuchungsheldOTPTicket>(ticketCollectionName)
      .deleteMany({ timestamp: { $lt: expiredBefore } });
    await mongo()
      .collection<OTPSendLimit>(sendLimitCollectionName)
      .deleteMany({ lastSentAt: { $lt: new Date(Date.now() - sendWindowMs) } });
    logger.info({ deletedCount: result.deletedCount }, 'deleted expired otps');
  };

  interval(1000 * 60 * 60 * 6)
    .pipe(
      catchError(error => {
        console.error('Error in OTP cleanup scheduler:', error);
        return EMPTY;
      }),
    )
    .subscribe(() => {
      cleanup().catch(error => {
        console.error('Error during OTP cleanup:', error);
      });
    });

  if (baseConfig.get('DEPLOYMENT') === 'development')
    cleanup().catch(err => {
      logger.error({ err }, 'Error during OTP cleanup');
    });
};
